import React, { useState } from "react";
import { useTranslation } from 'react-i18next';
import { AttentionIcon, Clear, SortedDown, SortedUp, SortingArrows } from "../common/ProjectIcons";
import { Destination, Source } from "../common/ObjectTypes";
import NoResults from "../common/NoResults";
import Pagination from "../common/Pagination";
import Spinner from "../common/Spinner";
import { page_size } from "../../api/apiUtils";
import { useTimezone } from "../../context/TimezoneContext";
import { formatSourceDatetime } from "../sources/SourceHelperFunctions";
import { tableClasses, paginationClasses, filterClasses } from "../common/tailwindClassnames";
import GlobalSearch from "../common/GlobalSearch";
import SelectInput from "../common/SelectInput";

type FilterOption = { label: string; value: string };

type ConnectionSourceProps = {
    sources: Source[],
    totalSources: number,
    globalSearchStr: string,
    take: number,
    offset: number,
    onPageClicked: Function,
    loadingSources: boolean,
    pageLoaded: boolean,
    destinationUrls: string[],
    loadingDestinationUrls: boolean,
    destinationUrlFilterValue: FilterOption | null,
    onChangeGlobalSourceSearch: Function,
    onChangeDestinationUrlFilter: Function,
    requestSortSources: Function,
}

const ConnectionDetailsSource = ({
    sources,
    totalSources,
    globalSearchStr,
    take,
    offset,
    onPageClicked,
    pageLoaded,
    loadingSources,
    destinationUrls,
    loadingDestinationUrls,
    destinationUrlFilterValue,
    onChangeGlobalSourceSearch,
    onChangeDestinationUrlFilter,
    requestSortSources,
}: ConnectionSourceProps) => {
    const { t } = useTranslation();
    const { timezone } = useTimezone();
    const [sortDirection, setSortDirection] = useState(false);
    const [sortByColumn, setSortByColumn] = useState("external_id");

    return (
        <div className="mt-8">
            <div className="flex flex-row justify-between items-center w-full">
                <label className="text-secondary-gray dark:text-gray-lines transition-colors duration-300">{t("The individual sets of data coming from the provider.")}</label>
            </div>
            <div className="flex flex-col my-3">
                {/* Horizontal filter bar */}
                <div className="flex flex-row gap-3 mb-3 flex-wrap">
                    <div className="flex flex-col gap-1">
                        <span className="text-sm font-semibold text-off-black dark:text-white">{t("Name")}</span>
                        <GlobalSearch onChangeGlobalSearch={onChangeGlobalSourceSearch} savedString={globalSearchStr} />
                    </div>
                    <div className="flex flex-col gap-1">
                        <span className="text-sm font-semibold text-off-black dark:text-white">{t("Destination")}</span>
                        <SelectInput
                            name="destination_url__in"
                            options={destinationUrls.map(url => {
                                let hostname = url;
                                try { hostname = new URL(url).hostname; } catch {}
                                return { label: hostname, value: url };
                            })}
                            value={destinationUrlFilterValue}
                            onChange={onChangeDestinationUrlFilter}
                            isMulti={false}
                            isClearable={true}
                            isSearchable={true}
                            isDisabled={false}
                            isLoading={loadingDestinationUrls}
                            className="w-48"
                            variant="connectionFilter"
                        />
                    </div>
                </div>
                {/* Filter chips */}
                {(globalSearchStr || destinationUrlFilterValue) &&
                    <div className="flex flex-row flex-wrap gap-2 mb-3">
                        {globalSearchStr && <span className={filterClasses.tag}>
                            {globalSearchStr}
                            <span className="cursor-pointer text-dark-green dark:text-dark-green-100" onClick={() => onChangeGlobalSourceSearch('')}><Clear /></span>
                        </span>}
                        {destinationUrlFilterValue && <span className={filterClasses.tag}>
                            {destinationUrlFilterValue.label}
                            <span className="cursor-pointer text-dark-green dark:text-dark-green-100" onClick={() => onChangeDestinationUrlFilter(null)}><Clear /></span>
                        </span>}
                    </div>
                }
                <div className="flex flex-col w-full">
                    <label className="text-off-black dark:text-white transition-colors duration-300">{t("Total Sources")}: {totalSources}</label>
                    <table className={`mt-3 ${tableClasses.container} cursor-pointer`}>
                            <thead className={tableClasses.header}>
                                <tr>
                                    <th className={tableClasses.headerCell}>
                                        {t("External ID")}
                                        <span className={tableClasses.sortIcon} onClick={() => {
                                            requestSortSources("external_id", sortDirection)
                                            setSortDirection(!sortDirection)
                                            setSortByColumn("external_id")
                                        }}>
                                            {sortByColumn === "external_id" ?
                                                sortDirection ? <SortedUp /> : <SortedDown />
                                                : <SortingArrows />}
                                        </span>
                                    </th>
                                    <th className={tableClasses.headerCell}>
                                        {t("Destination")}
                                    </th>
                                    <th className={tableClasses.headerCell}>
                                        {t("Date Created")}
                                        <span className={tableClasses.sortIcon} onClick={() => {
                                            requestSortSources("created_at", sortDirection)
                                            setSortDirection(!sortDirection)
                                            setSortByColumn("created_at")
                                        }}>
                                            {sortByColumn === "created_at" ?
                                                sortDirection ? <SortedUp /> : <SortedDown />
                                                : <SortingArrows />}
                                        </span>
                                    </th>
                                </tr>
                            </thead>
                            {loadingSources ? <tbody>
                                <tr>
                                    <td colSpan={3} className={`${tableClasses.cell} text-center`}>
                                        <Spinner />
                                    </td>
                                </tr>
                            </tbody> : sources.length < 1 ? <tbody><tr><td colSpan={3} className="py-16">
                                <NoResults
                                    headerMsg={t("No Results Found")}
                                    labelMsg={t("You may need to configure your first source with this connection.")}
                                />
                            </td></tr></tbody> : <tbody className="text-sm">
                                {sources.map((source: Source) => {
                                    return (
                                        <tr key={source.id} className={tableClasses.row}>
                                            <td className={tableClasses.cell}>
                                                {source.status === "healthy" ? "" : <AttentionIcon />}{source.external_id}
                                            </td>
                                            <td className={tableClasses.cell}>
                                                {source.destinations.map((dest: Destination) => {
                                                    let dest_url: string | URL;
                                                    try {
                                                        dest_url = new URL(dest.base_url).hostname
                                                    } catch (err) {
                                                        dest_url = ""
                                                    }
                                                    return (
                                                        <p key={dest.id} className="text-blue-basic hover:text-blue-700 dark:text-dark-blue-300 dark:hover:text-light-blue-300 cursor-pointer no-underline hover:underline transition-colors duration-300">{dest_url}</p>
                                                    )
                                                })}
                                            </td>
                                            <td className={tableClasses.cell}>
                                                {formatSourceDatetime(source.created_at, timezone)}
                                            </td>
                                        </tr>
                                    )
                                })}
                            </tbody>}
                        </table>
                    {totalSources > page_size && <Pagination
                        showText={true}
                        handlePageClick={onPageClicked}
                        pageCount={Math.ceil(totalSources / take)}
                        resultStart={offset + 1}
                        resultEnd={(offset + take) < totalSources ? (offset + take) : totalSources}
                        totalCount={totalSources}
                        take={take}
                        loading={!pageLoaded}
                        mainclassname={paginationClasses.container}
                    />}
                </div>
            </div>
        </div>
    )
};

export default ConnectionDetailsSource;
